import { format } from 'date-fns';
import { isString } from 'lodash';
import { DateTime } from 'luxon';

export enum DATE_FORMATS {
	DETAIL_DATE = 'pp dd.MM.yyyy',
	UI = 'd MMMM yyyy',
	UI_SHORT = 'd MMM yyyy',
	UI_LONG = 'd MMMM yyyy, HH:mm:ss',
	EXCEL = 'dd.MM.yyyy',
	FILE_NAME = 'dd-MM-yyyy',
	CALENDAR_DATE = 'dd/MM/yyyy',
	BE_DATE = 'yyyy-MM-dd',
	BE_DATE_TIME = "yyyy-MM-dd'T'HH:mm:ss.SSS",
	DATE_TIME = 'dd/MM/yyyy (HH:mm)',
	DATE_TIME_ALT = 'dd MMMM yyyy, HH:mm',
	FULL_DATE_TIME = 'dd/MM/yyyy (HH:mm:ss)',
	MONTH_DAY_YEAR = 'MMM dd, yyyy',
	TIME = 'HH:mm a',
	DATE_PICKER = 'DD/MM/YYYY',
	TIMELINE_DATE_TIME = 'd MMM, HH:mm a',
	TIMELINE_EVENT_DATE_TIME = 'd MMM, hh:mmaaa',
	DATE_MONTH_YEAR = 'dd, MMM yyyy',
	DATE_FULL_MONTH_YEAR = 'dd MMMM yyyy',
	UTC_TIME_ON_DATE = "HH:mm:ss 'UTC on' MMMM dd, yyyy",
}

export const formatDateToBE = (date: Date) => {
	return date.toISOString();
};

export const getFullDayRange = (date: Date) => {
	const startOfDay = new Date(date);
	startOfDay.setUTCHours(0, 0, 0, 0);

	const endOfDay = new Date(date);
	endOfDay.setUTCHours(23, 59, 59, 999);

	return {
		startOfDay: startOfDay.toISOString(),
		endOfDay: endOfDay.toISOString(),
	};
};

export const formatDateToBEIgnoreTimezone = (date: Date) => {
	return `${format(date, DATE_FORMATS.BE_DATE)}T00:00:00.000Z`;
};

// Counterpart to formatDateToBEIgnoreTimezone: rebuilds a BE date-only value
// (UTC midnight) from its UTC calendar parts as a local Date, so it renders as
// the same calendar day in every timezone instead of shifting a day west of UTC.
export const parseBEDateIgnoreTimezone = (stringDate: string): Date => {
	const date = new Date(stringDate);
	return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const DD_MM_YYYY = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Parses a `DD/MM/YYYY` string typed into a date field, as a local-midnight Date.
 *
 * Mantine's DateInput parses typed input with bare `new Date(value)` unless given a
 * `dateParser`, which reads `DD/MM/YYYY` as US `MM/DD/YYYY` — "13/09/2026" is rejected and
 * "05/09/2026" silently becomes 9 May. Pass this to every DD/MM/YYYY DateInput.
 *
 * Returns an Invalid Date for anything that is not a real calendar day, so schema
 * validation rejects it rather than a rolled-over date slipping through (31/02 -> 3 March).
 */
export const parseUiDateDdMmYyyy = (input: string): Date => {
	const match = DD_MM_YYYY.exec(input.trim());
	if (!match) {
		return new Date(NaN);
	}

	const day = Number(match[1]);
	const month = Number(match[2]);
	const year = Number(match[3]);

	const parsed = new Date(year, month - 1, day);
	const isRealCalendarDay =
		parsed.getFullYear() === year &&
		parsed.getMonth() === month - 1 &&
		parsed.getDate() === day;

	return isRealCalendarDay ? parsed : new Date(NaN);
};

// Narrow an instant to its UTC calendar date, as a Date pinned to UTC midnight.
// Feed this to a Prisma `@db.Date` column so the stored day is the UTC calendar date
// of the instant, independent of driver truncation semantics.
export const getUtcDateOnly = (date: Date): Date =>
	new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

export const formatDateToUI = (date?: Date | null, dateFormat: DATE_FORMATS = DATE_FORMATS.UI) => {
	if (!date) {
		return;
	}
	return format(date, dateFormat);
};

export const formatOneSchemaDateToIsoString = (date: string): string => {
	const [day, month, year] = date.split('/').map(Number);

	if (typeof day === 'undefined' || typeof month === 'undefined' || typeof year === 'undefined') {
		throw new Error('Invalid date format');
	}

	const utcDate = new Date(Date.UTC(year, month - 1, day));
	return utcDate.toISOString();
};

export const formatStringDateToUI = (stringDate?: string, dateFormat: string = DATE_FORMATS.UI) => {
	if (!stringDate) {
		return;
	}

	return format(new Date(stringDate), dateFormat);
};

export const formatStringDateToUiWithFallback = (
	stringDate?: string,
	dateFormat: string = DATE_FORMATS.UI,
	fallBackString = ''
) => {
	return formatStringDateToUI(stringDate, dateFormat) ?? fallBackString;
};

// Timezone-safe display of a date-only (BE) value. Renders the stored UTC
// calendar date identically in every viewer timezone (same convention as formatBirthday),
// instead of running the value through a local-timezone `new Date()` that can shift a day.
// Use for date-only fields (transaction date, etc.); keep formatStringDateToUiWithFallback
// for genuine instants shown in viewer-local time.
export const formatBEDateToUiWithFallback = (
	stringDate?: string | null,
	dateFormat: string = DATE_FORMATS.UI,
	fallBackString = ''
): string => {
	if (!stringDate) {
		return fallBackString;
	}
	return format(parseBEDateIgnoreTimezone(stringDate), dateFormat);
};

export const formatDateToFileName = (date: Date) => {
	return format(date, DATE_FORMATS.FILE_NAME);
};

export const getDate18YearsAgo = (): Date =>
	new Date(new Date().setFullYear(new Date().getFullYear() - 18));

export const convertDateToTimeZone = (date: Date, offsetMinute: number): Date => {
	const offsetMs: number = offsetMinute * 60 * 1000;
	const localDate: Date = new Date(date.getTime() - offsetMs);

	return localDate;
};

export const formatBirthday = (
	stringDate?: string | null,
	dateFormat: string = DATE_FORMATS.UI
): string => {
	if (!stringDate) {
		return '';
	}

	return format(parseBEDateIgnoreTimezone(stringDate), dateFormat);
};

export const isIsoDateTimeString = (value: unknown): boolean => {
	if (!isString(value)) {
		return false;
	}
	return DateTime.fromISO(value, { zone: 'utc' }).isValid;
};

export const formatStringDateToUtc = (value: string) =>
	DateTime.fromISO(value, { zone: 'utc' }).toFormat("d MMM yyyy, HH:mm 'UTC'");

// Relative-to-now label, e.g. "2 days ago", "3 months ago", "in 5 minutes".
export const formatStringDateToRelative = (value: string): string | undefined =>
	DateTime.fromISO(value, { zone: 'utc' }).toRelative() ?? undefined;

export const isIsoDateInPastUtcDay = (value: string): boolean =>
	DateTime.fromISO(value, { zone: 'utc' }).startOf('day') < DateTime.utc().startOf('day');
